import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, Stack, Avatar, Chip, TextField, MenuItem, CircularProgress } from '@mui/material';
import SearchIcon        from '@mui/icons-material/Search';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import CancelIcon        from '@mui/icons-material/Cancel';
import WatchLaterIcon    from '@mui/icons-material/WatchLater';
import * as XLSX         from 'xlsx';
import jsPDF             from 'jspdf';
import autoTable         from 'jspdf-autotable';
import { useTranslation } from 'react-i18next';
import presenceService   from '../../services/presenceService';
import apiService        from '../../services/apiServices';
import PageHeader        from '../common/PageHeader';
import { ReportPage, ExportMenu } from './ReportKit';
import { activeWorkers } from '../../utils/workerStatus';

const todayStr   = () => new Date().toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toTimeString().slice(0, 5);
}
function fmtHHMM(min) {
  const m = Math.max(0, Math.round(min));
  return `${Math.floor(m / 60)}:${('0' + (m % 60)).slice(-2)}`;
}
function initials(name) {
  return (name || '').split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2);
}
function breakMin(breaks) {
  return (breaks || []).reduce((s, b) => {
    const st = b.start ? new Date(b.start).getTime() : NaN;
    const en = b.end   ? new Date(b.end).getTime()   : NaN;
    return s + (!isNaN(st) && !isNaN(en) && en > st ? Math.round((en - st) / 60000) : 0);
  }, 0);
}

const LATE_THRESHOLD = '09:00'; // hyrja pas 09:00 konsiderohet vonë

function attendStatus(checkIn, checkOut) {
  if (!checkIn) return { label: 'Mungues', color: 'error' };
  const t = fmtTime(checkIn);
  if (t > LATE_THRESHOLD) return { label: 'Vonë', color: 'warning' };
  return { label: 'I pranishëm', color: 'success' };
}

function eventsToSessions(events) {
  const groups = new Map();
  (events || []).forEach(e => {
    const at  = new Date(e.At || e.at);
    if (isNaN(at)) return;
    const day = at.toISOString().slice(0, 10);
    const id  = String(e.WorkerId || e.workerId || '');
    if (!id) return;
    const key = day + '__' + id;
    if (!groups.has(key)) groups.set(key, { day, workerId: id, checkIn: null, checkOut: null, breaks: [] });
    const g = groups.get(key);
    const type = (e.Type || e.type || '').toLowerCase().replace(/-/g, '_');
    if (type === 'checkin' || type === 'check_in' || type === 'in') {
      if (!g.checkIn || at < new Date(g.checkIn)) g.checkIn = at.toISOString();
    } else if (type === 'checkout' || type === 'check_out' || type === 'out') {
      g.checkOut = at.toISOString();
    } else if (type === 'break_start') {
      g.breaks.push({ start: at.toISOString() });
    } else if (type === 'break_end') {
      for (let i = g.breaks.length - 1; i >= 0; i--) {
        if (!g.breaks[i].end) { g.breaks[i].end = at.toISOString(); break; }
      }
    }
  });
  return Array.from(groups.values());
}

export default function AttendanceReport() {
  const { t } = useTranslation();
  const [dateFrom,    setDateFrom]    = useState(monthStart());
  const [dateTo,      setDateTo]      = useState(todayStr());
  const [fWorker,     setFWorker]     = useState('');
  const [fDept,       setFDept]       = useState('');
  const [fStatus,     setFStatus]     = useState('');
  const [loading,     setLoading]     = useState(false);
  const [events,      setEvents]      = useState([]);
  const [workers,     setWorkers]     = useState([]);
  const [departments, setDepartments] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [wRes, dRes, evts] = await Promise.all([
        apiService().get('workers'),
        apiService().get('departments'),
        presenceService().getRange(dateFrom, dateTo),
      ]);
      if (wRes.success) setWorkers(wRes.data || []);
      if (dRes.success) setDepartments(dRes.data || []);
      setEvents(evts || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const workerById = useCallback(id => workers.find(w => String(w.Id || w.id) === String(id)), [workers]);
  const wName      = useCallback(id => { const w = workerById(id); return w ? `${w.FirstName || ''} ${w.LastName || ''}`.trim() : ''; }, [workerById]);
  const wDeptId    = useCallback(id => { const w = workerById(id); return w ? String(w.Department || w.department_id || '') : ''; }, [workerById]);
  const wDeptName  = useCallback(id => { const w = workerById(id); return w ? (w.DepartmentName || '') : ''; }, [workerById]);

  // Lista rënëse është ZGJEDHJE, jo të dhëna: i larguari s'ka pse të zgjidhet.
  // Harta id→emër më lart mbetet e plotë, që rreshtat e periudhës kur ai ishte
  // ende në punë të mos dalin pa emër.
  const deptWorkers = useMemo(() =>
    activeWorkers(fDept ? workers.filter(w => String(w.Department || w.department_id || '') === String(fDept)) : workers)
  , [workers, fDept]);

  const sessions = useMemo(() => eventsToSessions(events), [events]);

  const rows = useMemo(() => {
    let s = sessions.filter(r => r.day >= dateFrom && r.day <= dateTo);
    if (fWorker) s = s.filter(r => String(r.workerId) === String(fWorker));
    if (fDept)   s = s.filter(r => wDeptId(r.workerId) === String(fDept));
    const mapped = s.map(r => {
      const start   = r.checkIn  ? new Date(r.checkIn).getTime()  : NaN;
      const end     = r.checkOut ? new Date(r.checkOut).getTime() : NaN;
      const totalRaw = !isNaN(start) && !isNaN(end) ? Math.max(0, Math.round((end - start) / 60000)) : 0;
      const brkMin  = breakMin(r.breaks);
      const presMin = Math.max(0, totalRaw - brkMin);
      const status  = attendStatus(r.checkIn, r.checkOut);
      return { ...r, name: wName(r.workerId), deptName: wDeptName(r.workerId), brkMin, presMin, status };
    });
    if (fStatus) return mapped.filter(r => r.status.label === fStatus);
    return mapped.sort((a, b) => b.day.localeCompare(a.day) || a.name.localeCompare(b.name));
  }, [sessions, fWorker, fDept, fStatus, dateFrom, dateTo, wName, wDeptName, wDeptId]);

  const stats = useMemo(() => ({
    present: rows.filter(r => r.status.label === 'I pranishëm').length,
    late:    rows.filter(r => r.status.label === 'Vonë').length,
    absent:  rows.filter(r => r.status.label === 'Mungues').length,
    avgH:    rows.length > 0 ? rows.reduce((s, r) => s + r.presMin, 0) / rows.length : 0,
  }), [rows]);

  const HEADERS = [t('report.date'), t('report.worker'), t('report.department'), t('report.checkIn'), t('report.checkOut'), t('report.duration'), t('report.totalHours'), t('report.status')];
  const expRows  = () => rows.map(r => [r.day, r.name, r.deptName, fmtTime(r.checkIn), fmtTime(r.checkOut), r.brkMin, fmtHHMM(r.presMin), r.status.label]);

  const exportCSV = () => {
    const lines = [HEADERS, ...expRows()].map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(','));
    const a = document.createElement('a'); a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(lines.join('\n'));
    a.download = `attendance-${todayStr()}.csv`; a.click();
  };
  const exportExcel = () => {
    const ws = XLSX.utils.aoa_to_sheet([HEADERS, ...expRows()]);
    ws['!cols'] = [12, 22, 18, 8, 8, 12, 10, 14].map(w => ({ wch: w }));
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Prezenca');
    XLSX.writeFile(wb, `attendance-${todayStr()}.xlsx`);
  };
  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.text('Raporti i Prezencës', 14, 15);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.text(`Periudha: ${dateFrom} — ${dateTo}`, 14, 22);
    autoTable(doc, { head: [HEADERS], body: expRows(), startY: 27, styles: { fontSize: 8 }, headStyles: { fillColor: [2, 132, 199] } });
    doc.save(`attendance-${todayStr()}.pdf`);
  };

  const STATUS_CHIP = { 'I pranishëm': 'success', 'Vonë': 'warning', 'Mungues': 'error' };

  return (
    <ReportPage>
      <PageHeader title={t('reports.attendance')} subtitle={t('reports.attendanceDesc')} />

      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" fontWeight={700}>{t('report.filters')}</Typography>
          {(fWorker || fDept || fStatus) && <Chip size="small" label={t('report.clear')} onClick={() => { setFWorker(''); setFDept(''); setFStatus(''); }} onDelete={() => { setFWorker(''); setFDept(''); setFStatus(''); }} />}
          <Chip size="small" label={t('report.refresh')} onClick={load} variant="outlined" sx={{ ml: 'auto' }} />
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}><TextField type="date" size="small" fullWidth label={t('report.from')} InputLabelProps={{ shrink: true }} value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></Grid>
          <Grid item xs={6} sm={3}><TextField type="date" size="small" fullWidth label={t('report.to')} InputLabelProps={{ shrink: true }} value={dateTo} onChange={e => setDateTo(e.target.value)} /></Grid>
          <Grid item xs={6} sm={2}>
            <TextField select size="small" fullWidth label={t('report.department')} value={fDept} onChange={e => { setFDept(e.target.value); setFWorker(''); }}>
              <MenuItem value=""><em>{t('report.all')}</em></MenuItem>
              {departments.map(d => <MenuItem key={d.id || d.Id} value={d.id || d.Id}>{d.name || d.Name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField select size="small" fullWidth label={t('report.worker')} value={fWorker} onChange={e => setFWorker(e.target.value)}>
              <MenuItem value=""><em>{t('report.all')}</em></MenuItem>
              {deptWorkers.map(w => <MenuItem key={w.Id || w.id} value={w.Id || w.id}>{w.FirstName || ''} {w.LastName || ''}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField select size="small" fullWidth label={t('report.status')} value={fStatus} onChange={e => setFStatus(e.target.value)}>
              <MenuItem value=""><em>{t('report.all')}</em></MenuItem>
              {[{k:'I pranishëm',l:t('report.presentLabel')},{k:'Vonë',l:t('report.lateLabel')},{k:'Mungues',l:t('report.absentLabel')}].map(s => <MenuItem key={s.k} value={s.k}>{s.l}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: t('report.presentLabel'), val: stats.present, color: 'success.main', icon: <CheckCircleIcon sx={{ fontSize: 18 }} /> },
          { label: t('report.lateLabel'), val: stats.late, color: 'warning.main', icon: <WatchLaterIcon sx={{ fontSize: 18 }} /> },
          { label: t('report.absentLabel'), val: stats.absent, color: 'error.main', icon: <CancelIcon sx={{ fontSize: 18 }} /> },
          { label: t('report.avgHoursPerDay'), val: fmtHHMM(stats.avgH), color: 'primary.main', icon: null },
        ].map(c => (
          <Grid item xs={6} sm={3} key={c.label}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
              {c.icon && <Box sx={{ color: c.color, mb: 0.5 }}>{c.icon}</Box>}
              <Typography variant="h5" fontWeight={800} color={c.color}>{c.val}</Typography>
              <Typography variant="caption" color="text.secondary">{c.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {t('report.dailyPresence')} <Chip size="small" label={`${rows.length}`} sx={{ ml: 1, fontSize: '0.72rem' }} />
          </Typography>
          <Stack direction="row" spacing={1}>
            <ExportMenu onCsv={exportCSV} onExcel={exportExcel} onPdf={exportPDF} />
          </Stack>
        </Stack>
        <Box sx={{ overflowX: 'auto', maxHeight: 520, overflowY: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {HEADERS.map(h => <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.78rem', bgcolor: 'grey.50', whiteSpace: 'nowrap' }}>{h}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={HEADERS.length} align="center" sx={{ py: 5 }}><CircularProgress size={26} /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={HEADERS.length} align="center" sx={{ py: 5, color: 'text.secondary' }}>{t('report.noData')}</TableCell></TableRow>
              ) : rows.map((r, i) => (
                <TableRow key={r.workerId + r.day} hover sx={{ bgcolor: i % 2 ? '#FAFAFA' : 'white' }}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.day}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.65rem', bgcolor: r.status.label === 'Mungues' ? 'error.main' : r.status.label === 'Vonë' ? 'warning.main' : 'success.main' }}>{initials(r.name)}</Avatar>
                      <Typography variant="body2" fontWeight={600}>{r.name || '—'}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{r.deptName || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem', color: r.checkIn ? '#16a34a' : 'text.disabled', fontWeight: r.checkIn ? 700 : 400 }}>{fmtTime(r.checkIn)}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem', color: r.checkOut ? '#dc2626' : 'text.disabled', fontWeight: r.checkOut ? 700 : 400 }}>{fmtTime(r.checkOut)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{r.brkMin > 0 ? r.brkMin : '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.82rem' }}>{r.presMin > 0 ? fmtHHMM(r.presMin) : '—'}</TableCell>
                  <TableCell><Chip size="small" color={STATUS_CHIP[r.status.label] || 'default'} label={r.status.label === 'I pranishëm' ? t('report.presentLabel') : r.status.label === 'Vonë' ? t('report.lateLabel') : t('report.absentLabel')} sx={{ fontSize: '0.72rem' }} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </ReportPage>
  );
}
